import { type ChangeEvent, useCallback } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { TextInput, TextInputSize } from '@/components/ui/text-input'
import {
  caretPositionForDigitCount,
  countDigitsBefore,
  formatPhoneNumberInput,
  getPhoneEntryMaxDigits,
} from '@/utils/phone-number'

interface PhoneNumberFormFieldProps {
  name: string
  disabled?: boolean
  label?: string
  placeholder?: string
  required?: boolean
  size?: TextInputSize
  withReservedFooter?: boolean
}

export const PhoneNumberFormField = ({
  name,
  disabled,
  label,
  placeholder,
  required,
  size = 'small',
  withReservedFooter,
}: PhoneNumberFormFieldProps) => {
  const { field, fieldState } = useController({ name })
  const {
    resetField,
    clearErrors,
    formState: { defaultValues },
  } = useFormContext()

  const maxDigits = getPhoneEntryMaxDigits()
  const rawValue = typeof field.value === 'string' ? field.value : ''
  const displayValue = formatPhoneNumberInput(rawValue.replace(/\D/g, ''), maxDigits)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberInput(e.target.value.replace(/\D/g, ''), maxDigits)

    // The "Clear" button dispatches a synthetic event with a plain-object target
    // (no real DOM node), so caret handling only applies to genuine input edits.
    if (e.target instanceof HTMLInputElement) {
      const digitsBeforeCaret = countDigitsBefore(
        e.target.value,
        e.target.selectionStart ?? e.target.value.length,
      )
      const newCaret = caretPositionForDigitCount(formatted, digitsBeforeCaret)
      e.target.value = formatted
      e.target.setSelectionRange(newCaret, newCaret)
    }

    field.onChange(formatted)
    if (!formatted) clearErrors(name)
  }

  const handleRestore = () => resetField(name)

  // Note: restoring only makes sense when there is a saved value that was changed - a create form
  // (empty default) already has Clear.
  const canRestore = !!defaultValues?.[name] && fieldState.isDirty

  const handleRef = useCallback((input: HTMLInputElement | null) => field.ref(input), [field])

  return (
    <TextInput
      ref={handleRef}
      disabled={disabled}
      error={fieldState.error?.message}
      name={field.name}
      value={displayValue}
      clearable={!disabled}
      label={label}
      placeholder={placeholder}
      required={required}
      size={size}
      withReservedFooter={withReservedFooter}
      onBlur={field.onBlur}
      onChange={handleChange}
      onRestore={!disabled && canRestore ? handleRestore : undefined}
    />
  )
}
